import React, { useState } from 'react';
import { useInView } from '../hooks/useInView';

const steps = [
  { num: 1, title: 'Intake & AI Extract', desc: 'Scanned PDF → structured rows', color: '#4C6EF5', icon: '⬆' },
  { num: 2, title: 'Execution & Checks', desc: 'Real-time limit checks', color: '#7B3FE4', icon: '✓' },
  { num: 3, title: 'Supervisor Review', desc: 'Completeness sign-off', color: '#9B59B6', icon: '👁' },
  { num: 4, title: 'QA Review', desc: 'Engine exceptions only', color: '#00C9B1', icon: '🔍' },
  { num: 5, title: 'Approval', desc: 'E-signed disposition', color: '#00B894', icon: '✍' },
  { num: 6, title: 'QP Certify (EU)', desc: 'Annex 16 certification', color: '#F5A623', icon: '🏅' },
  { num: 7, title: 'Release', desc: 'ERP disposition handoff', color: '#F39C12', icon: '🚀' },
];

export default function Workflow() {
  const [active, setActive] = useState(null);
  const [ref, inView] = useInView();

  return (
    <section id="workflow" style={{
      padding: '120px 24px',
      background: 'linear-gradient(180deg, transparent, rgba(22,27,36,0.8), transparent)',
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div ref={ref} style={{
          textAlign: 'center', marginBottom: 72,
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}>
          <p style={{
            fontSize: 12, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase',
            color: '#00C9B1', marginBottom: 14, fontFamily: "'JetBrains Mono', monospace",
          }}>The Workflow</p>
          <h2 style={{
            fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 900, lineHeight: 1.08,
            letterSpacing: '-0.03em', color: '#fff', marginBottom: 20,
          }}>One governed state machine.</h2>
          <p style={{
            fontSize: 18, color: 'rgba(255,255,255,0.5)', maxWidth: 560, margin: '0 auto',
          }}>
            Every step role-bound, e-signed, audited, site-scoped — and configurable, never hard-coded.
          </p>
        </div>

        {/* Pipeline */}
        <div style={{
          display: 'flex',
          gap: 0,
          overflowX: 'auto',
          paddingBottom: 12,
          scrollbarWidth: 'none',
        }}>
          {steps.map((step, i) => (
            <React.Fragment key={step.num}>
              <div
                style={{
                  flex: '0 0 auto',
                  minWidth: 140,
                  background: active === i
                    ? `linear-gradient(135deg, ${step.color}20, ${step.color}10)`
                    : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${active === i ? step.color + '50' : 'rgba(255,255,255,0.07)'}`,
                  borderRadius: 14,
                  padding: '24px 16px',
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  opacity: inView ? 1 : 0,
                  transform: inView ? 'translateY(0)' : 'translateY(32px)',
                  transitionDelay: `${i * 80}ms`,
                  textAlign: 'center',
                }}
                onClick={() => setActive(active === i ? null : i)}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: step.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 900, color: '#fff',
                  margin: '0 auto 14px',
                  boxShadow: `0 4px 16px ${step.color}40`,
                }}>{step.num}</div>
                <p style={{
                  fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 6, lineHeight: 1.3,
                }}>{step.title}</p>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>{step.desc}</p>
              </div>
              {i < steps.length - 1 && (
                <div style={{
                  flex: '0 0 auto', display: 'flex', alignItems: 'center',
                  color: 'rgba(255,255,255,0.2)', fontSize: 18, padding: '0 6px',
                }}>›</div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Config tags */}
        <div style={{
          marginTop: 36,
          background: 'rgba(245,166,35,0.06)',
          border: '1px solid rgba(245,166,35,0.15)',
          borderRadius: 14, padding: '18px 24px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              background: '#F5A623', color: '#000', padding: '3px 10px', borderRadius: 100,
              whiteSpace: 'nowrap',
            }}>◆ Configurable at every step</span>
            {['extraction templates', 'parameter limits', 'role & e-sign rules', 'engine thresholds', 'approval & dual-sign gates', 'per-jurisdiction rules'].map(t => (
              <span key={t} style={{
                fontSize: 12, color: 'rgba(255,255,255,0.5)',
                fontFamily: "'JetBrains Mono', monospace",
              }}>· {t}</span>
            ))}
          </div>
        </div>

        {/* Exception branch note */}
        <p style={{
          marginTop: 16, fontSize: 12, color: 'rgba(255,255,255,0.3)',
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          Off-pipeline EXCEPTION / deviation branch (OOS, CAPA) quarantines the batch and rejoins on resolution.
        </p>
      </div>
    </section>
  );
}
